'use client';

import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSystemLoading } from '../contexts/LoadingContext';
import { apiService } from '../services/apiService';
import {
  AIFraudOverviewResponse,
  AIFraudCasesResponse,
  DashboardOverviewResponse,
  SpecialtyAnalysis
} from '../types/dashboard';
import FraudOverview from './FraudOverview';
import FraudCases from './FraudCases';
import ClinicalOverview from './ClinicalOverview';
import Analytics from './Analytics';
import FileUpload from './FileUpload';
import ProviderChart from './ProviderChart';
import ProviderTableView from './ProviderTableView';
import DataQuality from './DataQuality';
import BrandLogo from './BrandLogo';
import PhysicianReportsView, { PhysicianReportType } from './PhysicianReportsView';
import ProviderReportsView from './ProviderReportsView';
import {
  FileBarChart2,
  Stethoscope,
  ShieldAlert,
  Activity,
  DollarSign,
  Award,
  Building2,
  Layers,
  ChevronRight,
  Scale
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, BarChart, LineChart, LabelList
} from 'recharts';

// ─────────────────────────────────────────────────────────────────────────────
//  Shared UI primitives (Copied from Dashboard.tsx)
// ─────────────────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '10px 14px', fontSize: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
      <p style={{ margin: '0 0 6px', color: '#94a3b8', fontWeight: 600 }}>{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ margin: '2px 0', color: p.color || '#fff', fontWeight: 700 }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, style }: { title: string; subtitle?: string; children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', ...style }}>
    <div style={{ marginBottom: '14px' }}>
      <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>{title}</p>
      {subtitle && <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 0' }}>{subtitle}</p>}
    </div>
    {children}
  </div>
);

const SectionDivider = ({ label }: { label: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0 20px' }}>
    <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
    <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', whiteSpace: 'nowrap' }}>{label}</span>
    <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
  </div>
);

const C = {
  blue: '#0ea5e9',
  indigo: '#6366f1',
  pink: '#ec4899',
  amber: '#f59e0b',
  green: '#22c55e',
  red: '#ef4444',
  teal: '#14b8a6',
};
const BAR_PALETTE = ['#0ea5e9', '#6366f1', '#ec4899', '#f59e0b', '#22c55e', '#14b8a6', '#a855f7', '#ef4444', '#64748b', '#f97316'];
const fmtM = (v: number) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}K`;


type TabType =
  | 'overview'
  | 'clinical'
  | 'fraud'
  | 'cases'
  | 'analytics'
  | 'upload'
  | 'data-quality'
  | 'providers-table'
  | 'providers-charts'
  | 'physician-benchmarking'
  | 'physician-fraud'
  | 'physician-inpatient-outpatient'
  | 'physician-change-amount'
  | 'physician-performance'
  | 'provider-reports-fraud'
  | 'provider-reports-services';

type SectionKey = 'providers';

const ProviderDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const [aiFraudOverview, setAIFraudOverview] = useState<AIFraudOverviewResponse | null>(null);
  const [aiFraudCases, setAIFraudCases] = useState<AIFraudCasesResponse | null>(null);
  const [dashboardOverview, setDashboardOverview] = useState<DashboardOverviewResponse | null>(null);
  const [specialties, setSpecialties] = useState<SpecialtyAnalysis[]>([]);
  const [overviewCharts, setOverviewCharts] = useState<any>(null);
  const { setSystemLoading } = useSystemLoading();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    providers: false,
  });

  const [isReportsOpen, setIsReportsOpen] = useState<boolean>(true);
  const [expandedReportCategories, setExpandedReportCategories] = useState<Record<string, boolean>>({
    physician: true,
    provider: false,
  });

  const isPhysicianReportActive = [
    'physician-benchmarking',
    'physician-fraud',
    'physician-inpatient-outpatient',
    'physician-change-amount',
    'physician-performance',
  ].includes(activeTab);

  const isProviderReportActive = [
    'provider-reports-fraud',
    'provider-reports-services',
  ].includes(activeTab);

  const isReportsActive = isPhysicianReportActive || isProviderReportActive;

  useEffect(() => {
    if (isPhysicianReportActive) {
      setIsReportsOpen(true);
      setExpandedReportCategories((prev) => ({ ...prev, physician: true }));
    } else if (isProviderReportActive) {
      setIsReportsOpen(true);
      setExpandedReportCategories((prev) => ({ ...prev, provider: true }));
    }
  }, [isPhysicianReportActive, isProviderReportActive]);

  const toggleReports = () => {
    setIsReportsOpen((prev) => !prev);
  };

  const toggleReportCategory = (catId: string) => {
    setExpandedReportCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  const reportCategories = [
    {
      id: 'physician',
      label: 'Physician Reports',
      icon: Stethoscope,
      badge: null,
      items: [
        { id: 'physician-benchmarking' as TabType, label: 'Peer Benchmarking', icon: Scale },
        { id: 'physician-fraud' as TabType, label: 'Fraud Detection', icon: ShieldAlert },
        { id: 'physician-inpatient-outpatient' as TabType, label: 'In/Out-Patient Analysis', icon: Activity },
        { id: 'physician-change-amount' as TabType, label: 'Change of Amount', icon: DollarSign },
        { id: 'physician-performance' as TabType, label: 'Physician Performance', icon: Award },
      ],
    },
    {
      id: 'provider',
      label: 'Provider Reports',
      icon: Building2,
      badge: 'Prepared',
      items: [
        { id: 'provider-reports-fraud' as TabType, label: 'Provider Fraud Overview', icon: ShieldAlert },
        { id: 'provider-reports-services' as TabType, label: 'Facility Service Analysis', icon: Layers },
      ],
    },
  ];

  const toggleSection = (section: SectionKey) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => { loadDashboardData(); }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setSystemLoading(true, 'Loading provider analytics...');
      setError(null);
      const [aiOverview, aiCases, overview, specialtiesData, chartsData] = await Promise.all([
        apiService.getAIFraudOverview(0),
        apiService.getAIFraudCases(0.3, 100000),
        apiService.getDashboardOverview(),
        apiService.getSpecialtiesData(50),
        apiService.getDashboardOverviewCharts()
      ]);
      setAIFraudOverview(aiOverview);
      setAIFraudCases(aiCases);
      setDashboardOverview(overview);
      setSpecialties(specialtiesData);
      setOverviewCharts(chartsData);
    } catch (e: any) {
      setError(e?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setSystemLoading(false);
    }
  };

  const getFraudValue = (key: string): any => {
    if (!aiFraudOverview) return null;
    return (aiFraudOverview as any)[key];
  };

  const highRiskCount = getFraudValue('fraud_claims') || 0;
  const fraudRate = getFraudValue('fraud_rate') || getFraudValue('fraudRate') || 0;
  const totalAnalyzed = getFraudValue('total_claims') || getFraudValue('totalClaims') || 0;
  const isProvider = user?.company_type?.toLowerCase() === 'provider';
  // Fallback: if top_payers is empty or missing, try top_providers (where the data currently is)
  const topEntitiesData = ((overviewCharts?.top_payers && overviewCharts.top_payers.length > 0)
    ? overviewCharts.top_payers
    : (overviewCharts?.top_providers || [])).slice(0, 3);
  const grossAmountTrend = overviewCharts?.gross_amount_trend || [];
  const fraudTrend = overviewCharts?.fraud_trend || [];
  const serviceStackData = overviewCharts?.service_category_trend || [];

  const mapCategoryName = (code: string) => {
    const map: Record<string, string> = {
      'P': 'Pharmacy',
      'I': 'Investigation',
      'C': 'Consultation',
      'T': 'Treatment/Procedure',
      'X': 'Consumables'
    };
    return map[code.toUpperCase()] || code;
  };

  // ── Main tabs (same as Payer) ──
  /**
   * Main navigation tabs configuration.
   * Includes dynamic badges for Fraud Rate and High Risk counts.
   */
  const mainTabs = [
    { id: 'overview' as TabType, name: 'Overview', badge: null, iconPath: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { id: 'clinical' as TabType, name: 'Clinical Analytics', badge: null, iconPath: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
    { id: 'fraud' as TabType, name: 'Fraud Detection', badge: fraudRate > 0 ? `${Number(fraudRate).toFixed(1)}%` : null, iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    { id: 'cases' as TabType, name: 'Suspicious Cases', badge: highRiskCount > 0 ? highRiskCount.toString() : null, iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
    { id: 'analytics' as TabType, name: 'Analytics', badge: null, iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
    { id: 'data-quality' as TabType, name: 'Data Quality', badge: null, iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    { id: 'upload' as TabType, name: 'Upload Data', badge: null, iconPath: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' },
  ];

  
  /**
   * Sidebar sections that can be expanded/collapsed.
   * Labels dynamically switch between 'Providers' and 'Payers' based on the user's role.
   */
  const collapsibleSections = isProvider ? [] : [
    {
      key: 'providers' as SectionKey,
      label: 'Providers',
      color: '#6366f1',
      iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
      subTabs: [
        { id: 'providers-table' as TabType, label: 'Table', iconPath: 'M3 10h18M3 14h18M3 6h18M3 18h18' },
        { id: 'providers-charts' as TabType, label: 'Charts', iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
      ],
    },
  ];
  

  /**
   * Reusable header component for dashboard sections.
   * Displays a themed icon, title, and subtitle with custom gradients.
   */
  const SectionHeader = ({ gradient, iconBg, iconPath, title, subtitle }: any) => (
    <div style={{ padding: '24px 32px', background: gradient, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{ width: '48px', height: '48px', background: iconBg, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
      </div>
      <div>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>{title}</h2>
        <p style={{ fontSize: '13px', color: '#64748b', margin: 0, marginTop: '2px' }}>{subtitle}</p>
      </div>
    </div>
  );

  const renderUserInfo = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
      <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: 'white', flexShrink: 0 }}>
        {user?.name.charAt(0)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</p>
        <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{user?.role}</p>
      </div>
    </div>
  );

  /* Loading */
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#f8fafc', borderRadius: '24px', border: '1px solid #e2e8f0', padding: '48px', textAlign: 'center', maxWidth: '420px', width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <div style={{ marginBottom: '32px', position: 'relative', display: 'inline-block' }}>
            <div style={{ width: '80px', height: '80px', border: '3px solid #e2e8f0', borderRadius: '50%', position: 'absolute', inset: 0 }} />
            <div style={{ width: '80px', height: '80px', border: '3px solid transparent', borderTopColor: '#6366f1', borderRightColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </div>
          </div>
          <h3 style={{ fontSize: '22px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>Loading Analytics</h3>
          <p style={{ fontSize: '14px', color: '#64748b' }}>Analyzing fraud patterns...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* Error */
  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ background: '#fef2f2', borderRadius: '24px', border: '1px solid #fecaca', padding: '40px', maxWidth: '480px', width: '100%' }}>
          <h3 style={{ fontSize: '22px', fontWeight: 700, color: '#1e293b', textAlign: 'center', marginBottom: '12px' }}>System Error</h3>
          <p style={{ fontSize: '14px', color: '#dc2626', textAlign: 'center', marginBottom: '28px' }}>{error}</p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={loadDashboardData} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', color: 'white', fontWeight: 600, fontSize: '14px', borderRadius: '12px', border: 'none', cursor: 'pointer' }}>Retry</button>
            <button onClick={() => window.location.reload()} style={{ flex: 1, padding: '14px', background: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '14px', borderRadius: '12px', border: '1px solid #e2e8f0', cursor: 'pointer' }}>Reload</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex' }}>

      {/* ══ SIDEBAR ══ */}
      <aside style={{ width: '260px', flexShrink: 0, background: '#ffffff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100vh', position: 'sticky', top: 0, boxShadow: '2px 0 8px rgba(0,0,0,0.04)', overflowY: 'auto', overflowX: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          <div style={{ padding: '6px 4px 10px' }}>
            <BrandLogo />
          </div>
          {renderUserInfo()}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>

          {/* Overview Tab (First Item) */}
          {mainTabs.slice(0, 1).map((tab) => {
            const isActive = activeTab === tab.id;

            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '11px 14px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: isActive ? 600 : 500,
                  transition: 'all 0.18s',
                  textAlign: 'left',
                  background: isActive
                    ? 'linear-gradient(135deg, #6366f1, #818cf8)'
                    : 'transparent',
                  color: isActive ? 'white' : '#64748b',
                  boxShadow: isActive ? '0 4px 14px rgba(99,102,241,0.25)' : 'none'
                }}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.iconPath} />
                </svg>
                <span style={{ flex: 1 }}>{tab.name}</span>
              </button>
            );
          })}

          {/* ══ REPORTS MAIN NAVIGATION TAB ══ */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' }}>
            <button
              onClick={toggleReports}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '11px 14px',
                borderRadius: '12px',
                border: isReportsActive ? '1px solid #c7d2fe' : 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: isReportsActive ? 700 : 600,
                transition: 'all 0.18s',
                textAlign: 'left',
                background: isReportsActive ? '#eef2ff' : 'transparent',
                color: isReportsActive ? '#4f46e5' : '#475569'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileBarChart2 size={18} color={isReportsActive ? '#4f46e5' : '#94a3b8'} />
                <span>Reports</span>
              </div>
              <ChevronRight
                size={16}
                color="#94a3b8"
                style={{
                  transform: isReportsOpen ? 'rotate(90deg)' : 'none',
                  transition: 'transform 0.2s'
                }}
              />
            </button>

            {/* Reports Tree */}
            {isReportsOpen && (
              <div style={{ paddingLeft: '14px', borderLeft: '2px solid #e2e8f0', margin: '4px 0 4px 12px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {reportCategories.map((category) => {
                  const CategoryIcon = category.icon;
                  const isCatExpanded = expandedReportCategories[category.id];
                  const isCatActive = category.id === 'physician' ? isPhysicianReportActive : isProviderReportActive;

                  return (
                    <div key={category.id} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <button
                        onClick={() => toggleReportCategory(category.id)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: isCatActive ? 700 : 600,
                          background: isCatActive ? '#f5f3ff' : 'transparent',
                          color: isCatActive ? '#4f46e5' : '#334155'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CategoryIcon size={15} color={isCatActive ? '#4f46e5' : '#64748b'} />
                          <span>{category.label}</span>
                        </div>
                        <ChevronRight
                          size={14}
                          color="#94a3b8"
                          style={{
                            transform: isCatExpanded ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.2s'
                          }}
                        />
                      </button>

                      {/* Sub-Reports */}
                      {isCatExpanded && (
                        <div style={{ paddingLeft: '12px', borderLeft: '2px solid #cbd5e1', margin: '2px 0 2px 10px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          {category.items.map((sub) => {
                            const isSubActive = activeTab === sub.id;
                            const SubIcon = sub.icon;

                            return (
                              <button
                                key={sub.id}
                                onClick={() => setActiveTab(sub.id)}
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '7px 10px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  fontWeight: isSubActive ? 700 : 500,
                                  background: isSubActive ? '#ede9fe' : 'transparent',
                                  color: isSubActive ? '#4338ca' : '#64748b',
                                  textAlign: 'left'
                                }}
                              >
                                <SubIcon size={14} color={isSubActive ? '#4338ca' : '#94a3b8'} />
                                <span>{sub.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Remaining Main Tabs */}
          {mainTabs.slice(1).map((tab) => {
            const isActive = activeTab === tab.id;
            const isUpload = tab.id === 'upload';

            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '11px 14px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: isActive || isUpload ? 600 : 500,
                  transition: 'all 0.18s',
                  textAlign: 'left',
                  background: isUpload
                    ? 'linear-gradient(135deg, #22c55e, #10b981)'
                    : isActive
                      ? 'linear-gradient(135deg, #6366f1, #818cf8)'
                      : 'transparent',
                  color: isUpload || isActive ? 'white' : '#64748b',
                  boxShadow: isUpload
                    ? '0 4px 14px rgba(34, 197, 94, 0.35)'
                    : isActive
                      ? '0 4px 14px rgba(99,102,241,0.25)'
                      : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isActive && !isUpload) {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#0f172a';
                  } else if (isUpload) {
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(34, 197, 94, 0.45)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive && !isUpload) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#64748b';
                  } else if (isUpload) {
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(34, 197, 94, 0.35)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ flexShrink: 0 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.iconPath} />
                </svg>
                <span style={{ flex: 1 }}>{tab.name}</span>
                {tab.badge && (
                  <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 700, background: isActive ? 'rgba(255,255,255,0.2)' : '#fef2f2', color: isActive ? 'white' : '#ef4444' }}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}

          <div style={{ margin: '10px 6px', borderTop: '1px solid #f1f5f9' }} />

          {collapsibleSections.map((section) => {
            const isExpanded = expandedSections[section.key];
            const isSectionActive = activeTab.startsWith('providers');
            return (
              <div key={section.key}>
                <button onClick={() => toggleSection(section.key)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, transition: 'all 0.18s', textAlign: 'left', background: isSectionActive ? `${section.color}14` : isExpanded ? '#f8fafc' : 'transparent', color: isSectionActive ? section.color : '#374151' }}
                  onMouseEnter={(e) => { if (!isSectionActive) e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseLeave={(e) => { if (!isSectionActive) e.currentTarget.style.background = isSectionActive ? `${section.color}14` : isExpanded ? '#f8fafc' : 'transparent'; }}
                >
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={section.iconPath} />
                  </svg>
                  <span style={{ flex: 1 }}>{section.label}</span>
                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2.5} style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                {isExpanded && (
                  <div style={{ paddingLeft: '14px', marginTop: '2px', marginBottom: '2px' }}>
                    <div style={{ borderLeft: `2px solid ${section.color}25`, paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      {section.subTabs.map((sub) => {
                        const isSubActive = activeTab === sub.id;
                        return (
                          <button key={sub.id} onClick={() => setActiveTab(sub.id)}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px', borderRadius: '9px', border: 'none', cursor: 'pointer', fontSize: '12.5px', fontWeight: isSubActive ? 600 : 400, transition: 'all 0.15s', textAlign: 'left', background: isSubActive ? section.color : 'transparent', color: isSubActive ? '#ffffff' : '#6b7280', boxShadow: isSubActive ? `0 3px 10px ${section.color}33` : 'none' }}
                            onMouseEnter={(e) => { if (!isSubActive) { e.currentTarget.style.background = `${section.color}12`; e.currentTarget.style.color = section.color; } }}
                            onMouseLeave={(e) => { if (!isSubActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; } }}
                          >
                            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isSubActive ? 2.2 : 1.8} style={{ flexShrink: 0 }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d={sub.iconPath} />
                            </svg>
                            <span>{sub.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '14px', borderTop: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', gap: '8px' }}>
          <button onClick={loadDashboardData} disabled={loading}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px 16px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', color: 'white', fontWeight: 600, fontSize: '13px', borderRadius: '12px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>
          <button onClick={logout} title="Logout"
            style={{ padding: '11px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" x2="9" y1="12" y2="12" />
            </svg>
          </button>
        </div>
      </aside>

      {/* ══ MAIN CONTENT ══ */}
      <main style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '28px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', margin: '0 0 4px' }}>Provider Dashboard</h1>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Real-time claims analysis & fraud pattern detection</p>
              </div>

              {/* KPI Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px', marginBottom: '24px' }}>
                {[
                  { label: 'Total Claims', value: dashboardOverview?.totalClaims?.toLocaleString() || '0', badge: 'LIVE', badgeBg: '#eff6ff', badgeColor: '#0ea5e9', badgeBorder: '#bfdbfe', iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', gradient: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', glow: 'rgba(14,165,233,0.2)', textColor: '#0f172a', sub: 'All records', subColor: '#16a34a' },
                  { label: 'High Risk', value: highRiskCount.toLocaleString(), badge: 'ALERT', badgeBg: '#fef2f2', badgeColor: '#ef4444', badgeBorder: '#fecaca', iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z', gradient: 'linear-gradient(135deg, #ef4444, #f87171)', glow: 'rgba(239,68,68,0.2)', textColor: '#dc2626', sub: 'Requires Review', subColor: '#ef4444' },
                  { label: isProvider ? 'Unique Payers' : 'Unique Providers', value: (isProvider ? (overviewCharts?.unique_payers || 0) : (overviewCharts?.unique_providers || 0)).toLocaleString(), badge: 'NETWORK', badgeBg: '#f0fdf4', badgeColor: '#16a34a', badgeBorder: '#bbf7d0', iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', gradient: 'linear-gradient(135deg, #16a34a, #22c55e)', glow: 'rgba(22,163,74,0.2)', textColor: '#15803d', sub: isProvider ? 'Active in network' : 'Treating facilities', subColor: '#16a34a' },
                  { label: 'Fraud Rate', value: `${Number(fraudRate).toFixed(1)}%`, badge: 'RATE', badgeBg: '#fffbeb', badgeColor: '#d97706', badgeBorder: '#fde68a', iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)', glow: 'rgba(245,158,11,0.2)', textColor: '#b45309', sub: 'AI Model', subColor: '#94a3b8' },
                  // { label: 'AI Analyzed', value: totalAnalyzed.toLocaleString(), badge: 'AI', badgeBg: '#f0fdf4', badgeColor: '#16a34a', badgeBorder: '#bbf7d0', iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', gradient: 'linear-gradient(135deg, #22c55e, #4ade80)', glow: 'rgba(34,197,94,0.2)', textColor: '#15803d', sub: 'Processed', subColor: '#16a34a' },
                ].map((card) => (
                  <div key={card.label} style={{ background: '#ffffff', borderRadius: '18px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <div style={{ width: '44px', height: '44px', background: card.gradient, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 14px ${card.glow}` }}>
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={card.iconPath} /></svg>
                      </div>
                      <span style={{ padding: '3px 10px', background: card.badgeBg, color: card.badgeColor, fontSize: '10px', fontWeight: 700, borderRadius: '999px', border: `1px solid ${card.badgeBorder}` }}>{card.badge}</span>
                    </div>
                    <p style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{card.label}</p>
                    <p style={{ fontSize: '28px', fontWeight: 800, color: card.textColor, margin: '0 0 8px', letterSpacing: '-0.5px' }}>{card.value}</p>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: card.subColor }}>{card.sub}</span>
                  </div>
                ))}
              </div>

              {/* Risk Distribution */}
              <div style={{ background: '#ffffff', borderRadius: '18px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 18px' }}>Risk Distribution</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {[
                    { label: 'Low Risk', pct: totalAnalyzed > 0 ? Math.max(0, ((totalAnalyzed - highRiskCount) / totalAnalyzed) * 100) : 0, color: '#22c55e' },
                    { label: 'High Risk', pct: totalAnalyzed > 0 ? (highRiskCount / totalAnalyzed) * 100 : 0, color: '#ef4444' },
                  ].map((item) => (
                    <div key={item.label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>{item.label}</span>
                        <span style={{ fontSize: '13px', color: item.color, fontWeight: 700 }}>{item.pct.toFixed(1)}%</span>
                      </div>
                      <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(item.pct, 100)}%`, background: item.color, borderRadius: '999px', transition: 'width 1s ease' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <SectionDivider label="Fraud & Financial Analytics" />

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <ChartCard title="Service Category Mix" subtitle="Monthly stacked breakdown of top categories">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={serviceStackData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                      {Object.keys(serviceStackData[0] || {}).filter(k => k !== 'month').map((cat, idx) => (
                        <Bar key={cat} dataKey={cat} name={mapCategoryName(cat)} stackId="a" fill={BAR_PALETTE[idx % BAR_PALETTE.length]} radius={idx === Object.keys(serviceStackData[0] || {}).length - 2 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
                    <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                        <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                    </div>
                    <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 4px' }}>Analytics Ready</h4>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Advanced patterns detected in current dataset</p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '16px' }}>
                <ChartCard title="Top 3 Payers by Claim Amount" subtitle="Ranked by gross billed amount">
                  <ResponsiveContainer width="100%" height={210}>
                    <BarChart data={topEntitiesData} layout="vertical" margin={{ top: 4, right: 48, left: 4, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={fmtM} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={72} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="amount" name="Amount" fill={C.green} radius={[0, 6, 6, 0]} maxBarSize={22}>
                        <LabelList dataKey="amount" position="right" formatter={fmtM} style={{ fontSize: '10px', fill: '#64748b', fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>
            </div>
          )}

          {/* CLINICAL */}
          {activeTab === 'clinical' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <SectionHeader gradient="linear-gradient(135deg, #f5f3ff, #ede9fe)" iconBg="linear-gradient(135deg, #8b5cf6, #a78bfa)" iconPath="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" title="Clinical Analytics" subtitle="Medical specialties and diagnosis patterns" />
              <div style={{ padding: '32px' }}><ClinicalOverview overview={dashboardOverview} specialties={specialties} /></div>
            </div>
          )}

          {/* FRAUD */}
          {activeTab === 'fraud' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <SectionHeader gradient="linear-gradient(135deg, #fef2f2, #fee2e2)" iconBg="linear-gradient(135deg, #ef4444, #f87171)" iconPath="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" title="AI Fraud Detection" subtitle="Machine learning powered fraud analysis" />
              <div style={{ padding: '32px' }}><FraudOverview fraudOverview={aiFraudOverview} /></div>
            </div>
          )}

          {/* CASES */}
          {activeTab === 'cases' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <SectionHeader gradient="linear-gradient(135deg, #eff6ff, #dbeafe)" iconBg="linear-gradient(135deg, #0ea5e9, #38bdf8)" iconPath="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" title="Suspicious Cases" subtitle="High-risk claims requiring immediate investigation" />
              <div style={{ padding: '32px' }}><FraudCases fraudCases={aiFraudCases} /></div>
            </div>
          )}

          {/* ANALYTICS */}
          {activeTab === 'analytics' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <SectionHeader gradient="linear-gradient(135deg, #f0fdf4, #dcfce7)" iconBg="linear-gradient(135deg, #22c55e, #4ade80)" iconPath="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" title="Analytics" subtitle="Specialties, diagnoses and cost breakdowns" />
              <div style={{ padding: '32px' }}><Analytics /></div>
            </div>
          )}

          {/* UPLOAD */}
          {activeTab === 'upload' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '32px' }}><FileUpload onUploadComplete={() => setActiveTab('overview')} /></div>
            </div>
          )}

          {/* DATA QUALITY */}
          {activeTab === 'data-quality' && (
            <div style={{ padding: '32px' }}><DataQuality /></div>
          )}

          {/* PROVIDERS TABLE */}
          {activeTab === 'providers-table' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <SectionHeader gradient="linear-gradient(135deg, #eef2ff, #e0e7ff)" iconBg="linear-gradient(135deg, #6366f1, #818cf8)" iconPath="M3 10h18M3 14h18M3 6h18M3 18h18" title={isProvider ? "Payers — Table" : "Providers — Table"} subtitle={isProvider ? "Detailed payer data table" : "Detailed provider data table"} />
              <div style={{ padding: '32px' }}><ProviderTableView /></div>
            </div>
          )}

          {/* PROVIDERS CHARTS */}
          {activeTab === 'providers-charts' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <SectionHeader gradient="linear-gradient(135deg, #eef2ff, #e0e7ff)" iconBg="linear-gradient(135deg, #6366f1, #818cf8)" iconPath="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" title={isProvider ? "Payers — Charts" : "Providers — Charts"} subtitle={isProvider ? "Visual payer analytics & performance" : "Visual provider analytics & performance"} />
              <div style={{ padding: '32px' }}><ProviderChart /></div>
            </div>
          )}

          {/* PHYSICIAN REPORTS */}
          {isPhysicianReportActive && (
            <div style={{ padding: '8px' }}>
              <PhysicianReportsView
                activeReport={activeTab as PhysicianReportType}
                setActiveReport={(rep) => setActiveTab(rep)}
              />
            </div>
          )}

          {/* PROVIDER REPORTS */}
          {isProviderReportActive && (
            <div style={{ padding: '8px' }}>
              <ProviderReportsView
                activeSubReport={activeTab}
              />
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default ProviderDashboard;